/**
 * Broadcast graphics layer — HTML/CSS overlays on top of the PROGRAM feed.
 *
 *   const gfx = createGraphicsLayer(programEl, config);
 *   gfx.pushEvents(newEvents);                 // each sim step (cards/subs/VAR context)
 *   gfx.sync(director, snapshot, stats, director.replay); // every frame
 *
 * The layer diffs DirectorState.graphics by instance id: DOM is created once
 * per graphic instance and only text/width values are touched per frame.
 * Everything scales with the container (1920×1080 design units via
 * container-query units), so the same layer works on a preview tile or full screen.
 */
import './graphics.css';
import type {
  DirectorState, GraphicInstance, GraphicKind, MatchConfig, MatchEvent, MatchStats, ReplayState, WorldSnapshot,
} from '../core/contracts';
import { clockParts, esc, indexPlayers, inkFor } from './format';
import { buildGraphic, type BuildCtx, type Built, type EventMemory } from './templates';

export interface GraphicsLayer {
  readonly root: HTMLElement;
  sync(state: DirectorState, snapshot: WorldSnapshot, stats: MatchStats, replay: ReplayState): void;
  /** Feed sim events so lower thirds know card colours, sub pairs, VAR reasons. */
  pushEvents(events: readonly MatchEvent[]): void;
  /** Fast branded wipe (~0.6s). Fully covers the frame at ~40–60% — swap feeds under it. */
  playStinger(): void;
  setConfig(config: MatchConfig): void;
  dispose(): void;
}

export interface GraphicsLayerOptions {
  /** Auto-play the stinger when replay.active flips (default true). */
  autoStinger?: boolean;
  /** Brand text in the stinger + scorebug badge (default "SD"). */
  brand?: string;
}

/** Graphics drawn by persistent elements rather than per-instance DOM. */
const PERSISTENT: ReadonlySet<GraphicKind> = new Set<GraphicKind>(['scorebug', 'clock', 'replay_bug']);
const OUT_MS = 650;

interface Live { inst: GraphicInstance; built: Built }

export function createGraphicsLayer(container: HTMLElement, config: MatchConfig, opts: GraphicsLayerOptions = {}): GraphicsLayer {
  const brand = esc(opts.brand ?? 'SD');
  if (getComputedStyle(container).position === 'static') container.style.position = 'relative';

  const host = document.createElement('div');
  host.className = 'sdg-host';
  host.innerHTML = `
    <div class="sdg-root">
      <div class="sdg-scorebug" aria-hidden="true">
        <div class="sdg-sb-badge"><span>${brand}</span></div>
        <div class="sdg-sb-team sdg-sb-home"><i class="sdg-sb-chip"></i><b class="sdg-sb-name"></b><span class="sdg-sb-reds"></span></div>
        <div class="sdg-sb-score"><b class="sdg-sb-h">0</b><i></i><b class="sdg-sb-a">0</b></div>
        <div class="sdg-sb-team sdg-sb-away"><b class="sdg-sb-name"></b><i class="sdg-sb-chip"></i><span class="sdg-sb-reds"></span></div>
        <div class="sdg-sb-clock"><b class="sdg-sb-time">00:00</b><span class="sdg-sb-period">1ST</span></div>
        <div class="sdg-sb-extra"><span></span></div>
      </div>
      <div class="sdg-stage"></div>
      <div class="sdg-replaybug" aria-hidden="true">
        <div class="sdg-rb-flag"><i class="sdg-rb-rec"></i><b>REPLAY</b></div>
        <div class="sdg-rb-speed"><span></span></div>
      </div>
      <div class="sdg-stinger" aria-hidden="true">
        <i class="sdg-st-band sdg-st-b1"></i>
        <div class="sdg-st-band sdg-st-b2"><div class="sdg-st-logo"><span>${brand}</span></div></div>
        <i class="sdg-st-band sdg-st-b3"></i>
      </div>
    </div>`;
  container.appendChild(host);
  const root = host.querySelector<HTMLElement>('.sdg-root')!;
  const q = <T extends HTMLElement = HTMLElement>(sel: string): T => root.querySelector<T>(sel)!;

  const sb = {
    el: q('.sdg-scorebug'),
    homeName: q('.sdg-sb-home .sdg-sb-name'), awayName: q('.sdg-sb-away .sdg-sb-name'),
    homeReds: q('.sdg-sb-home .sdg-sb-reds'), awayReds: q('.sdg-sb-away .sdg-sb-reds'),
    h: q('.sdg-sb-h'), a: q('.sdg-sb-a'),
    time: q('.sdg-sb-time'), period: q('.sdg-sb-period'),
    extra: q('.sdg-sb-extra'), extraText: q('.sdg-sb-extra span'),
  };
  const stage = q('.sdg-stage');
  const replayBug = q('.sdg-replaybug');
  const replaySpeed = q('.sdg-rb-speed span');
  const stinger = q('.sdg-stinger');

  let cfg = config;
  let players = indexPlayers(cfg);
  const memory: EventMemory = { cards: new Map(), subs: new Map(), lastVar: null, goals: new Map() };
  const live = new Map<number, Live>();
  const seen = new Set<number>();
  let lastReplayActive = false;
  let lastScore = { home: -1, away: -1 };
  let lastReds = { home: -1, away: -1 };
  let stingerTimer = 0;

  const applyConfig = (): void => {
    const s = root.style;
    s.setProperty('--home', cfg.home.primaryColor);
    s.setProperty('--home-2', cfg.home.secondaryColor);
    s.setProperty('--home-ink', inkFor(cfg.home.primaryColor));
    s.setProperty('--away', cfg.away.primaryColor);
    s.setProperty('--away-2', cfg.away.secondaryColor);
    s.setProperty('--away-ink', inkFor(cfg.away.primaryColor));
    sb.homeName.textContent = cfg.home.shortName;
    sb.awayName.textContent = cfg.away.shortName;
    players = indexPlayers(cfg);
  };
  applyConfig();

  const setText = (el: HTMLElement, s: string): void => { if (el.textContent !== s) el.textContent = s; };
  const toggle = (el: HTMLElement, cls: string, on: boolean): void => { if (el.classList.contains(cls) !== on) el.classList.toggle(cls, on); };
  const reds = (el: HTMLElement, n: number): void => { el.innerHTML = '<i></i>'.repeat(Math.min(n, 5)); };

  const pulse = (el: HTMLElement, cls: string): void => {
    el.classList.remove(cls);
    void el.offsetWidth; // restart animation
    el.classList.add(cls);
  };

  const removeLive = (id: number): void => {
    const l = live.get(id);
    if (!l) return;
    live.delete(id);
    const el = l.built.el;
    el.classList.add('is-out');
    window.setTimeout(() => el.remove(), OUT_MS);
  };

  const layer: GraphicsLayer = {
    root,
    sync(state, snapshot, stats, replay) {
      const ctx: BuildCtx = { config: cfg, players, snapshot, stats, memory };

      // --- scorebug (persistent element) ---
      const sbOn = state.graphics.some((g) => g.kind === 'scorebug');
      toggle(sb.el, 'is-on', sbOn);
      if (sbOn) {
        const { main, extra, period } = clockParts(snapshot);
        setText(sb.time, main);
        setText(sb.period, period);
        toggle(sb.extra, 'is-on', extra !== null);
        if (extra) setText(sb.extraText, extra);
        if (snapshot.score.home !== lastScore.home || snapshot.score.away !== lastScore.away) {
          const first = lastScore.home < 0;
          if (!first && snapshot.score.home !== lastScore.home) pulse(sb.h, 'is-flash');
          if (!first && snapshot.score.away !== lastScore.away) pulse(sb.a, 'is-flash');
          sb.h.textContent = String(snapshot.score.home);
          sb.a.textContent = String(snapshot.score.away);
          lastScore = { home: snapshot.score.home, away: snapshot.score.away };
        }
        if (stats.home.red !== lastReds.home) { reds(sb.homeReds, stats.home.red); lastReds.home = stats.home.red; }
        if (stats.away.red !== lastReds.away) { reds(sb.awayReds, stats.away.red); lastReds.away = stats.away.red; }
      }

      // --- replay bug + auto stinger ---
      if (replay.active !== lastReplayActive) {
        lastReplayActive = replay.active;
        if (opts.autoStinger ?? true) layer.playStinger();
      }
      toggle(replayBug, 'is-on', replay.active);
      if (replay.active) {
        const slow = replay.speed < 0.99;
        toggle(replayBug, 'is-slow', slow);
        setText(replaySpeed, slow ? `SLOW-MO ${replay.speed.toFixed(replay.speed < 0.3 ? 2 : 1).replace(/\.?0+$/, '')}×` : '');
      }

      // --- instance graphics (diff by id) ---
      seen.clear();
      for (const g of state.graphics) {
        if (PERSISTENT.has(g.kind)) continue;
        seen.add(g.id);
        let l = live.get(g.id);
        if (!l) {
          const built = buildGraphic(g.kind, g.subjectId, ctx);
          if (!built) continue;
          built.el.classList.add('sdg-g', `sdg-${g.kind.replace(/_/g, '-')}`);
          stage.appendChild(built.el);
          l = { inst: g, built };
          live.set(g.id, l);
        }
        l.built.update?.(ctx);
      }
      for (const id of live.keys()) if (!seen.has(id)) removeLive(id);
    },

    pushEvents(events) {
      for (const e of events) {
        if ((e.type === 'yellow_card' || e.type === 'red_card') && e.playerId) {
          const prev = memory.cards.get(e.playerId);
          memory.cards.set(e.playerId, {
            type: e.type === 'red_card' ? 'red' : 'yellow',
            secondYellow: e.type === 'red_card' && prev?.type === 'yellow',
            matchClock: e.matchClock,
          });
        } else if (e.type === 'substitution' && e.playerId) {
          memory.subs.set(e.playerId, { offId: e.secondaryPlayerId ?? null, matchClock: e.matchClock });
        } else if (e.type === 'var_check') {
          memory.lastVar = e;
        } else if (e.type === 'goal' && e.playerId) {
          memory.goals.set(e.playerId, e);
        }
      }
    },

    playStinger() {
      window.clearTimeout(stingerTimer);
      stinger.classList.remove('is-playing');
      void stinger.offsetWidth;
      stinger.classList.add('is-playing');
      stingerTimer = window.setTimeout(() => stinger.classList.remove('is-playing'), 800);
    },

    setConfig(c) {
      cfg = c;
      applyConfig();
      lastScore = { home: -1, away: -1 };
    },

    dispose() {
      window.clearTimeout(stingerTimer);
      live.clear();
      host.remove();
    },
  };
  return layer;
}

